import { createRouter, createWebHistory } from 'vue-router'

// --- PAGE COMPONENTS ---
// We DO NOT import layouts here. They are handled by App.vue and registered globally in main.js.

// Wizard Flow Components
import QuestionsText from '@/views/QuestionsText.vue'
import QuestionsUpload from '@/views/QuestionsUpload.vue'
import QuestionsQR from '@/views/QuestionsQR.vue'
import AnswersText from '@/views/AnswersText.vue'
import AnswersUpload from '@/views/AnswersUpload.vue'
import AnswersQR from '@/views/AnswersQR.vue'
import InstructionsEditor from '@/views/InstructionsEditor.vue'
import ProcessedAnswersOverview from '@/components/ProcessedAnswersOverview.vue'
import StudentWorkUpload from '@/views/StudentWorkUpload.vue'

// All Other Existing Pages
import Home from '@/components/Home.vue'
import UserSignup from '../components/UserSignup.vue'
import Login from '../components/UserLogin.vue'
import ProfilePage from '@/components/ProfilePage.vue'
import SuccessPage from '@/components/SuccessPage.vue'
import TermsOfService from '@/components/TermsOfService.vue'
import TestsOverview from '../components/TestsOverview.vue'
import TestDetail from '../components/TestDetails.vue'
import StudentPages from '../components/StudentPages.vue'
import TestPageDetail from '../components/TestPageDetail.vue'
import StudentResult from '@/components/StudentResult.vue'
import DetailedReport from '@/components/DetailedReport.vue'
import TestResults from '@/components/TestResults.vue'
import ProcessedSinglesOverview from '@/components/ProcessedSinglesOverview.vue'
import HandwritingToText from '@/components/HandwritingToText.vue'
import CameraCapture from '../components/CameraCapture.vue'
import GenericCameraUpload from '@/components/GenericCameraUpload.vue'
import EmailConfirmation from '@/views/EmailConfirmation.vue'
import ForgotPassword from '@/views/ForgotPassword.vue'
import ResetPassword from '@/views/ResetPassword.vue'

const routes = [
  // --- PUBLIC ROUTES (No Header/Footer) ---
  // The `meta: { layout: 'NoHeaderLayout' }` tells App.vue which layout component to use.
  {
    path: '/tests/:testId/students/:studentId/work',
    name: 'StudentWorkUpload',
    component: StudentWorkUpload,
    props: true, // This will pass testId and studentId as props
    meta: { layout: 'HeaderLayout', requiresAuth: true }
  },
  { path: '/', name: 'Home', component: Home, meta: { layout: 'NoHeaderLayout' } },
  { path: '/signup', name: 'Signup', component: UserSignup, meta: { layout: 'NoHeaderLayout' } },
  { path: '/login', name: 'Login', component: Login, meta: { layout: 'NoHeaderLayout' } },
  { path: '/confirm-email', name: 'EmailConfirmation', component: EmailConfirmation, meta: { layout: 'NoHeaderLayout' } },
  { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword, meta: { layout: 'NoHeaderLayout' } },
  { path: '/reset-password', name: 'ResetPassword', component: ResetPassword, meta: { layout: 'NoHeaderLayout' } },
  { path: '/camera', name: 'CameraCapture', component: CameraCapture, meta: { layout: 'NoHeaderLayout' } },
  { path: '/camera-upload', name: 'CameraUpload', component: GenericCameraUpload, meta: { layout: 'NoHeaderLayout' } },

  // --- AUTHENTICATED ROUTES (With Header/Footer) ---
  // These routes use the 'HeaderLayout'.
  { path: '/profile', name: 'Profile', component: ProfilePage, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/success', name: 'Success', component: SuccessPage, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/terms-of-service', name: 'TermsOfService', component: TermsOfService, meta: { layout: 'HeaderLayout' } },
  { path: '/testsoverview', name: 'TestsOverview', component: TestsOverview, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/tests/:testId', name: 'TestDetail', component: TestDetail, props: true, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/handwriting-to-text', name: 'HandwritingToText', component: HandwritingToText, meta: { layout: 'HandwritingToTextLayout', requiresAuth: true } },
  { path: '/tests/:testId/questionsandanswers/overview', name: 'ProcessedSinglesOverview', component: ProcessedSinglesOverview, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/tests/:testId/results', name: 'TestResults', component: TestResults, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/tests/:testId/students/:studentId/result', name: 'StudentResult', component: StudentResult, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/students/:studentId/pages', name: 'StudentPages', component: StudentPages, props: true, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  { path: '/students/:studentId/pages/:pageId', name: 'TestPageDetail', component: TestPageDetail, props: true, meta: { layout: 'HeaderLayout', requiresAuth: true } },
  {
    path: '/tests/:testId/students/:studentId/detailed-report',
    name: 'DetailedReport',
    component: DetailedReport,
    props: true, // This is important! It passes URL params as props.
    meta: { layout: 'HeaderLayout', requiresAuth: true }
  },

  // --- WIZARD ROUTES ---
  // All these routes use the 'TestCreationLayout' and are protected.
  // The `meta.step` is used by the layout to render the progress bar.
  { path: '/tests/:testId/questions/text', name: 'QuestionsText', component: QuestionsText, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 1 } },
  { path: '/tests/:testId/questions/upload', name: 'QuestionsUpload', component: QuestionsUpload, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 1 } },
  { path: '/tests/:testId/questions/qr', name: 'QuestionsQR', component: QuestionsQR, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 1 } },

  { path: '/tests/:testId/answers/text', name: 'AnswersText', component: AnswersText, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 2 } },
  { path: '/tests/:testId/answers/upload', name: 'AnswersUpload', component: AnswersUpload, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 2 } },
  { path: '/tests/:testId/answers/qr', name: 'AnswersQR', component: AnswersQR, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 2 } },

  { path: '/tests/:testId/instruction', name: 'InstructionsEditor', component: InstructionsEditor, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 3 } },

  { path: '/tests/:testId/answers/overview', name: 'AnswersOverview', component: ProcessedAnswersOverview, props: true, meta: { layout: 'TestCreationLayout', requiresAuth: true, step: 4 } }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to protect routes and prevent redirect loops
router.beforeEach((to, from, next) => {
  const isAuthenticated = !!localStorage.getItem('user-token')
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth && !isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  if (to.name === 'Login' && isAuthenticated) {
    next({ name: 'TestsOverview' })
    return
  }

  next()
})

export default router
